import {
  DEFAULT_MEMBER_GROUPS,
  normalizeRequiredText,
  type Choir,
  type InvitationCode,
  type JoinRequest,
  type JoinRequestStatus,
  type MemberGroup,
  type MemberGroupManagerGrant,
  type MemberRole,
  type Membership,
  type PermissionGroup,
  type PermissionGroupMember,
  type PermissionGroupPermission,
  type PermissionKey,
  type Season,
  type SeasonMembership,
  type UserAccount
} from './model';
import { SUPPORTED_PERMISSION_KEYS } from './permissions';

export const INVITATION_CODE_VALIDITY_DAYS = 7;
export { SUPPORTED_PERMISSION_KEYS } from './permissions';
const MS_PER_DAY = 24 * 60 * 60 * 1000;

export type InvitationCodeUsabilityProblem = 'deleted' | 'expired';

export interface InitialChoirSetupIds {
  readonly choirId: string;
  readonly seasonId: string;
  readonly membershipId: string;
  readonly seasonMembershipId: string;
  readonly memberGroupIds: readonly string[];
}

export interface InitialChoirSetup {
  readonly choir: Choir;
  readonly currentSeason: Season;
  readonly currentMembership: Membership;
  readonly currentSeasonMembership: SeasonMembership;
  readonly memberGroups: readonly MemberGroup[];
}

export function validatePermissionKeys(permissionKeys: readonly string[]): readonly PermissionKey[] {
  const seenPermissionKeys = new Set<string>();
  const validatedPermissionKeys: PermissionKey[] = [];

  for (const permissionKey of permissionKeys) {
    if (!SUPPORTED_PERMISSION_KEYS.includes(permissionKey as PermissionKey)) {
      throw new Error(`Unsupported permission key: ${permissionKey}`);
    }

    if (seenPermissionKeys.has(permissionKey)) {
      throw new Error(`Duplicate permission key: ${permissionKey}`);
    }

    seenPermissionKeys.add(permissionKey);
    validatedPermissionKeys.push(permissionKey as PermissionKey);
  }

  return validatedPermissionKeys;
}

export function buildPermissionGroup(input: {
  readonly permissionGroupId: string;
  readonly choirId: string;
  readonly seasonId: string;
  readonly name: string;
  readonly permissionKeys: readonly string[];
  readonly createdAt: string;
  readonly updatedAt: string;
}): {
  readonly permissionGroup: PermissionGroup;
  readonly permissions: readonly PermissionGroupPermission[];
} {
  const permissionKeys = validatePermissionKeys(input.permissionKeys);
  const permissionGroup: PermissionGroup = {
    permissionGroupId: input.permissionGroupId,
    choirId: input.choirId,
    seasonId: input.seasonId,
    name: normalizeRequiredText(input.name, 'Permission group name'),
    createdAt: input.createdAt,
    updatedAt: input.updatedAt
  };

  return {
    permissionGroup,
    permissions: permissionKeys.map((permissionKey) => ({
      permissionGroupId: input.permissionGroupId,
      permissionKey
    }))
  };
}

export function resolveEffectivePermissionKeys(input: {
  readonly memberRole: MemberRole;
  readonly seasonMembershipId: string | undefined;
  readonly choirId: string;
  readonly seasonId: string;
  readonly permissionGroups: readonly PermissionGroup[];
  readonly permissionGroupPermissions: readonly PermissionGroupPermission[];
  readonly permissionGroupMembers: readonly PermissionGroupMember[];
}): readonly PermissionKey[] {
  if (input.memberRole === 'admin') {
    return [...SUPPORTED_PERMISSION_KEYS];
  }

  if (input.seasonMembershipId === undefined) {
    return [];
  }

  const matchingPermissionGroupIds = new Set(
    input.permissionGroups
      .filter((group) => group.choirId === input.choirId && group.seasonId === input.seasonId)
      .map((group) => group.permissionGroupId)
  );
  const memberPermissionGroupIds = new Set(
    input.permissionGroupMembers
      .filter(
        (member) =>
          member.seasonMembershipId === input.seasonMembershipId &&
          matchingPermissionGroupIds.has(member.permissionGroupId)
      )
      .map((member) => member.permissionGroupId)
  );
  const effectivePermissionKeys = new Set<PermissionKey>();

  for (const permission of input.permissionGroupPermissions) {
    if (memberPermissionGroupIds.has(permission.permissionGroupId)) {
      effectivePermissionKeys.add(permission.permissionKey);
    }
  }

  return SUPPORTED_PERMISSION_KEYS.filter((permissionKey) => effectivePermissionKeys.has(permissionKey));
}

export function buildInitialChoirSetup(input: {
  readonly user: UserAccount;
  readonly choirName: string;
  readonly ids: InitialChoirSetupIds;
  readonly createdAt: string;
}): InitialChoirSetup {
  if (input.ids.memberGroupIds.length !== DEFAULT_MEMBER_GROUPS.length) {
    throw new Error('Default member group ids must match default member group definitions');
  }

  const choir: Choir = {
    choirId: input.ids.choirId,
    name: normalizeRequiredText(input.choirName, 'Choir name'),
    createdByUserId: input.user.userId,
    createdAt: input.createdAt
  };
  const currentSeason: Season = {
    seasonId: input.ids.seasonId,
    choirId: input.ids.choirId,
    name: '当前乐季',
    isCurrent: true,
    createdAt: input.createdAt
  };
  const currentMembership: Membership = {
    membershipId: input.ids.membershipId,
    userId: input.user.userId,
    choirId: input.ids.choirId,
    role: 'admin',
    createdAt: input.createdAt
  };
  const currentSeasonMembership: SeasonMembership = {
    seasonMembershipId: input.ids.seasonMembershipId,
    membershipId: input.ids.membershipId,
    choirId: input.ids.choirId,
    seasonId: input.ids.seasonId,
    nickname: input.user.email.split('@')[0],
    status: 'active',
    createdAt: input.createdAt
  };
  const memberGroups: readonly MemberGroup[] = DEFAULT_MEMBER_GROUPS.map((group, index) => ({
    memberGroupId: input.ids.memberGroupIds[index],
    choirId: input.ids.choirId,
    seasonId: input.ids.seasonId,
    name: group.name,
    isVocalPart: group.isVocalPart,
    isDefault: true,
    displayOrder: index,
    createdBySeasonMembershipId: null,
    createdAt: input.createdAt
  }));

  return {
    choir,
    currentSeason,
    currentMembership,
    currentSeasonMembership,
    memberGroups
  };
}

export function sameNormalizedText(left: string, right: string): boolean {
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

export function hasNormalizedNameCollision<T extends { readonly name: string } & Record<K, string>, K extends string>(
  items: readonly T[],
  name: string,
  idKey: K,
  ignoredId?: string
): boolean {
  return items.some((item) => item[idKey] !== ignoredId && sameNormalizedText(item.name, name));
}

export function nextDisplayOrder(items: readonly { readonly displayOrder: number }[]): number {
  return items.reduce((highest, item) => Math.max(highest, item.displayOrder), -1) + 1;
}

export function buildInvitationCode(input: {
  readonly invitationCodeId: string;
  readonly code: string;
  readonly choirId: string;
  readonly seasonId: string;
  readonly createdByMembershipId: string;
  readonly createdAt: string;
}): InvitationCode {
  const createdAtDate = new Date(input.createdAt);

  return {
    invitationCodeId: input.invitationCodeId,
    code: normalizeRequiredText(input.code, 'Invitation code'),
    choirId: input.choirId,
    seasonId: input.seasonId,
    status: 'active',
    createdByMembershipId: input.createdByMembershipId,
    createdAt: input.createdAt,
    deletedAt: null,
    expiresAt: new Date(createdAtDate.getTime() + INVITATION_CODE_VALIDITY_DAYS * MS_PER_DAY).toISOString()
  };
}

export function getInvitationCodeUsabilityProblem(
  invitationCode: InvitationCode,
  now: string
): InvitationCodeUsabilityProblem | null {
  if (invitationCode.status === 'deleted') {
    return 'deleted';
  }

  if (invitationCode.expiresAt <= now) {
    return 'expired';
  }

  return null;
}

export function buildPendingJoinRequest(input: {
  readonly joinRequestId: string;
  readonly userId: string;
  readonly invitationCode: InvitationCode;
  readonly nickname: string;
  readonly submittedAt: string;
}): JoinRequest {
  return {
    joinRequestId: input.joinRequestId,
    userId: input.userId,
    choirId: input.invitationCode.choirId,
    seasonId: input.invitationCode.seasonId,
    invitationCodeId: input.invitationCode.invitationCodeId,
    nickname: normalizeRequiredText(input.nickname, 'Nickname'),
    status: 'pending',
    submittedAt: input.submittedAt,
    reviewedByMembershipId: null,
    reviewedAt: null,
    rejectionReason: null
  };
}

export function isPendingJoinRequestForChoirSeason(
  joinRequest: JoinRequest,
  input: {
    readonly userId: string;
    readonly choirId: string;
    readonly seasonId: string;
  }
): boolean {
  return (
    joinRequest.userId === input.userId &&
    joinRequest.choirId === input.choirId &&
    joinRequest.seasonId === input.seasonId &&
    joinRequest.status === 'pending'
  );
}

export type ReviewedJoinRequest = JoinRequest & {
  readonly status: Exclude<JoinRequestStatus, 'pending'>;
  readonly reviewedByMembershipId: string;
  readonly reviewedAt: string;
  readonly rejectionReason: string | null;
};

export function buildReviewedJoinRequest(input: {
  readonly joinRequest: JoinRequest;
  readonly status: Exclude<JoinRequestStatus, 'pending'>;
  readonly reviewerMembershipId: string;
  readonly reviewedAt: string;
  readonly rejectionReason?: string;
}): ReviewedJoinRequest {
  return {
    ...input.joinRequest,
    status: input.status,
    reviewedByMembershipId: input.reviewerMembershipId,
    reviewedAt: input.reviewedAt,
    rejectionReason: input.status === 'approved' ? null : input.rejectionReason?.trim() || null
  };
}

export function canManageMemberGroupByRoleOrGrant(input: {
  readonly memberRole: MemberRole;
  readonly currentSeasonMembershipId: string | undefined;
  readonly memberGroupId: string;
  readonly managerGrants: readonly Pick<MemberGroupManagerGrant, 'memberGroupId' | 'seasonMembershipId'>[];
}): boolean {
  if (input.memberRole === 'admin') {
    return true;
  }

  if (!input.currentSeasonMembershipId) {
    return false;
  }

  return input.managerGrants.some(
    (grant) =>
      grant.memberGroupId === input.memberGroupId && grant.seasonMembershipId === input.currentSeasonMembershipId
  );
}

export function canCreatorAddFirstMemberToGroup(input: {
  readonly memberCount: number;
  readonly createdBySeasonMembershipId: string | null;
  readonly currentSeasonMembershipId: string | undefined;
}): boolean {
  return (
    input.memberCount === 0 &&
    input.currentSeasonMembershipId !== undefined &&
    input.createdBySeasonMembershipId === input.currentSeasonMembershipId
  );
}

export function shouldGrantFirstMemberGroupManager(input: {
  readonly wasEmpty: boolean;
  readonly targetMemberRole: MemberRole;
  readonly alreadyHasGrant: boolean;
}): boolean {
  return input.wasEmpty && input.targetMemberRole !== 'admin' && !input.alreadyHasGrant;
}
